/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 取得済みの ops 依頼ページを一覧描画用の 1 本の行リストへ畳むユーティリティ。id 一意を不変条件として担保する
 * @graph-connects none
 */

import type { OpsRequestsPage } from './api/ops.js';
import type { OpsRequest } from '../types/ops.js';

/**
 * 依頼の並びから **id 単位で最初の 1 件だけ**を残す（先に載る Firestore = SoT 側を採用）。id 重複を落とす
 * 規則はここが単一 source で、1 ページ内（API クライアント境界）と複数ページ跨ぎ（一覧描画）の両方が使う。
 *
 * 重複を落とすと行数は `total` と一致しなくなるが、`total` はサーバーが返す値をそのまま使う（表示側で
 * 数え直すとサーバーの不変条件が壊れたことを隠してしまう）。サーバー側の重複は
 * `list-ops-requests-use-case` の legacyOnly と、その回帰テストで検知する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼の並びから id 重複を落とす（先着＝SoT 側を残す）。1 ページ内と複数ページ跨ぎで同じ規則を使う
 * @graph-connects none
 */
export function dedupeOpsRequestsById(items: readonly OpsRequest[]): OpsRequest[] {
  const seen = new Set<string>();
  const rows: OpsRequest[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    rows.push(item);
  }
  return rows;
}

/**
 * 取得済みページを 1 本の行リストへ畳む。API は Firestore ブロックと BQ ミラーブロックを連結して返すため、
 * 同じ依頼が両方に載ると id が重複し、`key={r.id}` の一覧で React の再描画が壊れる（タブを切り替えても
 * 前のレンズの行が残る）。サーバー側でも重複は落とすが、表示側でも id の一意性を不変条件として担保する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 取得済みページを id 一意の行リストへ畳む（重複 id は先着＝SoT 側を残す）。key 衝突による一覧の描画崩れを防ぐ
 * @graph-connects dedupeOpsRequestsById [calls] ページ跨ぎの id 重複を落とす
 */
export function flattenOpsRequestPages(
  data: { pages: OpsRequestsPage[] } | undefined,
): OpsRequest[] {
  return dedupeOpsRequestsById((data?.pages ?? []).flatMap((page) => page.items));
}
